import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { Button } from "@/components/ui/button";
import { fmtNumber } from "@/lib/format";
import { ArrowRight, X } from "lucide-react";

function MiniPitch({ children }) {
  return (
    <div className="pitch-surface mx-auto" style={{ width: 220, aspectRatio: "68/100" }}>
      <div className="absolute inset-0">{children}</div>
    </div>
  );
}

function Dot({ x, y, color = "#b6ff2e", label, moving }) {
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-out"
      style={{ left: `${x}%`, top: `${y}%` }}>
      <div className="w-6 h-6 rounded-full grid place-items-center text-[9px] font-bold"
        style={{ background: color, color: "#0a0a0a", boxShadow: moving ? "0 0 16px rgba(182,255,46,0.8)" : "none" }}>
        {label}
      </div>
    </div>
  );
}

export function Onboarding({ manual = false, onClose }) {
  const { t } = useTranslation();
  const { state, set } = useDemo();
  const [step, setStep] = useState(0);
  const [counter, setCounter] = useState(0);
  const [moved, setMoved] = useState(false);

  useEffect(() => {
    if (step !== 1) { setCounter(0); setMoved(false); return; }
    const start = setTimeout(() => setMoved(true), 500);
    let raf;
    const t0 = Date.now();
    const animate = () => {
      const p = Math.min(1, (Date.now() - t0 - 500) / 1200);
      if (p > 0) setCounter(-0.14 * p);
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    const kick = setTimeout(() => { raf = requestAnimationFrame(animate); }, 600);
    return () => { clearTimeout(start); clearTimeout(kick); cancelAnimationFrame(raf); };
  }, [step]);

  const finish = () => {
    set("onboardingSeen", true);
    if (onClose) onClose();
  };

  const next = () => { if (step < 2) setStep(step + 1); else finish(); };

  return (
    <div className="fixed inset-0 z-[70] bg-neutral-950 flex flex-col grain" data-testid="onboarding">
      <div className="flex justify-between items-center p-4">
        <span className="font-display font-bold uppercase tracking-widest text-[#b6ff2e] text-sm">{t("common.appName")}</span>
        <button data-testid="onboarding-skip" onClick={finish} className="flex items-center gap-1 text-neutral-400 hover:text-neutral-100 text-sm">
          {manual ? <X size={18} /> : t("onboard.skip")}
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 max-w-xl mx-auto text-center">
        {step === 0 && (
          <div className="space-y-6 rise-in" key="s0">
            <MiniPitch>
              {[[50, 90], [20, 72], [40, 76], [60, 76], [80, 72], [30, 52], [50, 55], [70, 52], [25, 26], [50, 18], [75, 26]].map(([x, y]) => (
                <Dot key={`${x}-${y}`} x={x} y={y} label="" />
              ))}
            </MiniPitch>
            <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">{t("onboard.s1title")}</h1>
            <p className="text-neutral-400">{t("onboard.s1body")}</p>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6 rise-in" key="s1">
            <MiniPitch>
              <Dot x={moved ? 50 : 18} y={moved ? 54 : 30} label="10" moving={moved} />
            </MiniPitch>
            <div className="font-display text-6xl font-bold tabular" style={{ color: counter < -0.001 ? "#ff6b6b" : "#9ca3af" }}>
              {fmtNumber(counter, state.language, 2)}
            </div>
            <h2 className="font-display text-2xl font-bold">{t("onboard.s2title")}</h2>
            <p className="text-neutral-400">{t("onboard.s2body")}</p>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 rise-in w-full" key="s2">
            <div className="grid grid-cols-3 gap-3">
              {[["#b6ff2e", t("common.you")], ["#ffb547", t("common.coach")], ["#3fd7e5", t("common.ai")]].map(([c, label]) => (
                <div key={label} className="rounded-xl border border-neutral-800 p-3">
                  <div className="pitch-surface" style={{ aspectRatio: "68/100" }}>
                    <div className="absolute inset-0">
                      {[[50, 88], [30, 68], [50, 70], [70, 68], [35, 45], [65, 45], [50, 22]].map(([x, y]) => (
                        <div key={`${x}-${y}`} className="absolute w-2.5 h-2.5 rounded-full -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%`, background: c }} />
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 text-xs font-display uppercase tracking-wide" style={{ color: c }}>{label}</div>
                </div>
              ))}
            </div>
            <h2 className="font-display text-2xl font-bold">{t("onboard.s3title")}</h2>
            <p className="text-neutral-400">{t("onboard.s3body")}</p>
          </div>
        )}
      </div>

      <div className="p-6 flex flex-col items-center gap-4">
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-1.5 rounded-full transition-all" style={{ width: i === step ? 28 : 8, background: i === step ? "#b6ff2e" : "#3a3a3a" }} />
          ))}
        </div>
        <Button data-testid="onboarding-next" onClick={next}
          className="bg-[#b6ff2e] text-black hover:bg-[#a2e626] font-display uppercase tracking-wide px-8">
          {step < 2 ? t("onboard.next") : t("onboard.start")} <ArrowRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
}
